import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Tabs,
  Tab,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Switch,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Add, Edit, Delete, Search, PlayArrow,
  YouTube, VideoLibrary, Analytics, OndemandVideo,
  TrendingUp, Visibility, Favorite, NotificationsActive,
  Public as PublicIcon, People
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { videoService, categoryService } from '../services/api';
import VideoForm from '../components/VideoForm';
import SendNotificationModal from '../components/notifications/SendNotificationModal';
import ViewersModal from '../components/views/ViewersModal';

const Videos = () => {
  const { t, i18n } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // all, youtube, upload
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tabValue, setTabValue] = useState(0); // 0 = All Videos, 1 = Analytics
  const [deleteDialog, setDeleteDialog] = useState({ open: false, video: null, blockedMessage: '' });
  const [notificationModal, setNotificationModal] = useState({ open: false, video: null });
  const [previewDialog, setPreviewDialog] = useState({ open: false, video: null });
  const [viewersModal, setViewersModal] = useState({ open: false, video: null });

  useEffect(() => {
    fetchVideos();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchVideos();
  }, [search, statusFilter, categoryFilter, typeFilter, sortBy, sortDirection, currentPage]);

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAll();
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchVideos = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = {
        search,
        page: currentPage,
        limit: 12,
        sortBy,
        sortDirection
      };

      // Add status filter
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Add category filter
      if (categoryFilter !== 'all') {
        params.category = categoryFilter;
      }

      // Add type filter
      if (typeFilter === 'youtube') {
        params.hasYoutubeUrl = true;
      } else if (typeFilter === 'upload') {
        params.hasUpload = true;
      }

      const response = await videoService.getAll(params);
      setVideos(response.docs || []);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleCategoryFilterChange = (event) => {
    setCategoryFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  const handleDeleteClick = (video) => {
    setDeleteDialog({ open: true, video });
  };

  const handleDeleteConfirm = async () => {
    const videoToDelete = deleteDialog.video;
    try {
      await videoService.delete(videoToDelete._id);
      setDeleteDialog({ open: false, video: null, blockedMessage: '' });
      fetchVideos();
    } catch (error) {
      const data = error.response?.data;
      if (data?.error === 'has_attached_pdfs') {
        setDeleteDialog((prev) => ({
          ...prev,
          blockedMessage: t('videos.pdfsBlockingDelete', { count: data.count }),
        }));
        return;
      }
      console.error('Error deleting video:', error);
    }
  };

  const handleTogglePublic = async (video) => {
    try {
      await videoService.togglePublic(video._id, !video.isPublic);
      fetchVideos();
    } catch (error) {
      console.error('Error toggling public status:', error);
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return t('videos.active');
      case 'draft': return t('videos.draft');
      case 'unlisted': return t('videos.unlisted');
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'default';
      case 'unlisted': return 'warning';
      default: return 'default';
    }
  };

  const getThumbnailUrl = (video) => {
    // Priority: custom thumbnail > YouTube thumbnail > Vimeo thumbnail > placeholder
    if (video.thumbnailUrl) {
      return video.thumbnailUrl;
    }
    if (video.youtubeThumbUrl) {
      return video.youtubeThumbUrl;
    }
    if (video.vimeoThumbUrl) {
      return video.vimeoThumbUrl;
    }
    return null;
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          {t('videos.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setSelectedVideo(null);
            setFormOpen(true);
          }}
        >
          {t('videos.addVideo')}
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab
            icon={<VideoLibrary />}
            label={t('videos.allVideos')}
            iconPosition="start"
          />
          <Tab
            icon={<Analytics />}
            label={t('videos.analytics')}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={t('videos.searchPlaceholder')}
                  value={search}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth>
                  <InputLabel>{t('videos.status')}</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    label={t('videos.status')}
                  >
                    <MenuItem value="all">{t('common.all')}</MenuItem>
                    <MenuItem value="active">{t('videos.active')}</MenuItem>
                    <MenuItem value="draft">{t('videos.draft')}</MenuItem>
                    <MenuItem value="unlisted">{t('videos.unlisted')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth>
                  <InputLabel>{t('videos.type')}</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={handleTypeFilterChange}
                    label={t('videos.type')}
                  >
                    <MenuItem value="all">{t('common.all')}</MenuItem>
                    <MenuItem value="youtube">YouTube</MenuItem>
                    <MenuItem value="upload">{t('videos.upload')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('videos.category')}</InputLabel>
                  <Select
                    value={categoryFilter}
                    onChange={handleCategoryFilterChange}
                    label={t('videos.category')}
                  >
                    <MenuItem value="all">{t('videos.allCategories')}</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.nameGr} / {category.nameEn}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Videos Table */}
          <TableContainer component={Paper} sx={{
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 100 }}>{t('videos.thumbnail')}</TableCell>
                  <TableCell>{t('videos.titleField')}</TableCell>
                  <TableCell sx={{ width: 200 }}>{t('videos.category')}</TableCell>
                  <TableCell sx={{ width: 100 }}>{t('videos.type')}</TableCell>
                  <TableCell sx={{ width: 120 }}>{t('videos.status')}</TableCell>
                  <TableCell sx={{ width: 100 }}>{t('videos.views')}</TableCell>
                  <TableCell sx={{ width: 160 }}>{t('videos.createdAt')}</TableCell>
                  <TableCell sx={{ width: 80 }}>{t('videos.isPublic')}</TableCell>
                  <TableCell sx={{ width: 150 }}>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {t('common.loading')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : videos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {t('videos.noVideosFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  videos.map((video) => {
                    const thumbnailUrl = getThumbnailUrl(video);
                    const isYoutube = !!video.youtubeUrl;
                    const isVimeo = !isYoutube && !!video.vimeoUrl;

                    return (
                      <TableRow key={video._id} hover>
                        <TableCell>
                          <Avatar
                            variant="rounded"
                            src={thumbnailUrl}
                            sx={{ width: 80, height: 45, bgcolor: 'grey.300' }}
                          >
                            {!thumbnailUrl && <PlayArrow />}
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {video.titleGr || video.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {video.titleEn || ''}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {video.categories?.map(cat => cat.nameGr || cat.nameEn).join(', ') ||
                             video.category?.nameGr || video.category?.nameEn || t('videos.noCategory')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={isYoutube ? <YouTube /> : isVimeo ? <OndemandVideo /> : <VideoLibrary />}
                            label={isYoutube ? 'YouTube' : isVimeo ? 'Vimeo' : t('videos.upload')}
                            size="small"
                            color={isYoutube ? 'error' : isVimeo ? 'info' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(video.status)}
                            color={getStatusColor(video.status)}
                            size="small"
                            sx={{ minWidth: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Visibility fontSize="small" color="action" />
                            <Typography variant="body2">{video.views || 0}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {video.createdAt ? (
                            <Typography variant="body2">
                              {new Date(video.createdAt).toLocaleString(i18n.language === 'el' ? 'el-GR' : 'en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={t('videos.isPublicTooltip')}>
                            <Switch
                              size="small"
                              checked={video.isPublic || false}
                              onChange={() => handleTogglePublic(video)}
                              color="success"
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => setPreviewDialog({ open: true, video })}
                              color="success"
                              title={t('videos.watch')}
                              disabled={!video.videoUrl && !video.embedUrl}
                            >
                              <PlayArrow fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedVideo(video);
                                setFormOpen(true);
                              }}
                              color="primary"
                              title={t('common.edit')}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => setViewersModal({ open: true, video })}
                              color="info"
                              title={t('viewers.viewersButton')}
                            >
                              <People fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => setNotificationModal({ open: true, video })}
                              color="info"
                              title={t('notifications.sendNotification')}
                            >
                              <NotificationsActive fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(video)}
                              color="error"
                              title={t('common.delete')}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Table Pagination */}
          <TablePagination
            component="div"
            count={totalPages * 12} // Assuming 12 items per page from backend
            page={currentPage - 1} // TablePagination uses 0-based index
            onPageChange={(event, newPage) => setCurrentPage(newPage + 1)}
            rowsPerPage={12}
            onRowsPerPageChange={() => {}} // Keep fixed at 12 for now
            rowsPerPageOptions={[12]}
            labelRowsPerPage={t('common.rowsPerPage')}
          />
        </>
      )}

      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('videos.analyticsTitle')}
          </Typography>
          <Typography color="text.secondary">
            {t('videos.analyticsComingSoon')}
          </Typography>
        </Paper>
      )}

      <VideoForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        video={selectedVideo}
        onSuccess={fetchVideos}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, video: null, blockedMessage: '' })}>
        <DialogTitle>{t('videos.delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('videos.confirmDeleteVideo')}
          </DialogContentText>
          {deleteDialog.video && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {deleteDialog.video.titleGr || deleteDialog.video.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {deleteDialog.video.titleEn || ''}
              </Typography>
            </Box>
          )}
          {deleteDialog.blockedMessage && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {deleteDialog.blockedMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, video: null, blockedMessage: '' })}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={!!deleteDialog.blockedMessage}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, video: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {previewDialog.video?.titleGr || previewDialog.video?.title || ''}
        </DialogTitle>
        <DialogContent>
          {(previewDialog.video?.youtubeUrl || previewDialog.video?.vimeoUrl) && previewDialog.video?.embedUrl ? (
            <Box sx={{ position: 'relative', pt: '56.25%' }}>
              <iframe
                src={previewDialog.video.embedUrl}
                title="video preview"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
              />
            </Box>
          ) : previewDialog.video?.videoUrl ? (
            <video
              key={previewDialog.video.videoUrl}
              src={previewDialog.video.videoUrl}
              poster={previewDialog.video.thumbnailUrl || undefined}
              controls
              autoPlay
              style={{ width: '100%', maxHeight: '70vh', backgroundColor: '#000' }}
            />
          ) : (
            <Typography color="text.secondary">{t('videos.noVideosFound')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog({ open: false, video: null })}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Notification Modal */}
      {notificationModal.video && (
        <SendNotificationModal
          open={notificationModal.open}
          onClose={() => setNotificationModal({ open: false, video: null })}
          contentType="video"
          contentId={notificationModal.video._id}
          prefilledTitleGr={notificationModal.video.titleGr || notificationModal.video.title}
          prefilledTitleEn={notificationModal.video.titleEn || ''}
          prefilledBodyGr="Νέο βίντεο διαθέσιμο"
          prefilledBodyEn="New video available"
        />
      )}

      {/* Viewers Modal */}
      {viewersModal.video && (
        <ViewersModal
          open={viewersModal.open}
          onClose={() => setViewersModal({ open: false, video: null })}
          contentType="video"
          contentId={viewersModal.video._id}
          contentTitle={viewersModal.video.titleGr || viewersModal.video.titleEn || viewersModal.video.title}
        />
      )}
    </Box>
  );
};

export default Videos;